import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import Logo from '../components/Logo';

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading'); // 'loading', 'valid', 'invalid', 'success'
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setStatus('invalid');
        setError('Invalid reset link. No token provided.');
        return;
      }

      try {
        await axios.get(`${API_URL}/api/auth/verify-reset-token/${token}`);
        setStatus('valid');
      } catch (error) {
        setStatus('invalid');
        setError(error.response?.data?.detail || 'Invalid or expired reset link.');
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setStatus('success');
    } catch (error) {
      setError(error.response?.data?.detail || 'Password reset failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-civote flex items-center justify-center p-4">
      <div className="bg-white rounded-card shadow-card w-full max-w-md p-8" data-testid="reset-password-page">
        <div className="flex flex-col items-center mb-6">
          <Logo size="large" />
          <h1 className="text-2xl font-bold text-gray-800 mt-4 font-rubik">Reset Password</h1>
        </div>

        {status === 'loading' && (
          <div className="py-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600 font-rubik">Validating reset link...</p>
          </div>
        )}

        {status === 'invalid' && (
          <div className="py-8 text-center" data-testid="reset-invalid">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2 font-rubik">Invalid Reset Link</h2>
            <p className="text-red-600 font-rubik mb-6">{error}</p>
            <Link 
              to="/" 
              className="btn-primary inline-block"
            >
              Back to Login
            </Link>
          </div>
        )}

        {status === 'valid' && (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
            <p className="text-gray-600 text-sm font-rubik mb-4">
              Enter your new password below. Make sure it's at least 6 characters long.
            </p>

            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-card text-sm font-rubik" data-testid="reset-error">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary focus:border-primary font-rubik"
                required
                minLength={6}
                maxLength={64}
                data-testid="new-password-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary focus:border-primary font-rubik"
                required
                minLength={6}
                maxLength={64}
                data-testid="confirm-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={saving}
              className="w-full btn-primary py-3 disabled:bg-gray-400"
              data-testid="reset-password-btn"
            >
              {saving ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </form>
        )}

        {status === 'success' && (
          <div className="py-8 text-center" data-testid="reset-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2 font-rubik">Password Reset!</h2>
            <p className="text-gray-600 font-rubik mb-6">
              Your password has been successfully reset. You can now login with your new password.
            </p>
            <button
              onClick={() => navigate('/')}
              className="btn-primary"
              data-testid="go-to-login-btn"
            >
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ResetPasswordPage;
